import { useEffect, useRef } from "react";

import { animate, canAnimate, duration, easing } from "@/shared/lib";

/**
 * Салют (FE-14) — разовый разлёт частиц поверх кружка состояния.
 *
 * Появился ради одного экрана: покупка робота — то, ради чего пользователь
 * сюда пришёл, и завершаться она должна не галочкой, а событием. Волна по
 * кольцу у `ScreenState` для этого слишком тиха, а выдавать её всем зелёным
 * экранам подряд нельзя — тогда праздник обесценится (см. комментарий про
 * `celebrate` в ScreenState).
 *
 * Правила слоя движения соблюдены: анимируются только `transform` и
 * `opacity`, длительности и кривые берутся токенами, никаких зависимостей —
 * Web Animations API. Разлёт детерминирован (углы раскладываются золотым
 * углом): случайность здесь ничего не добавляет, а воспроизводимость нужна
 * и глазу, и тесту.
 */
interface CelebrationProps {
  /**
   * Акцент частиц — цвет робота из админки. Без него салют красится
   * токеном прибыли: hex в коде запрещён, а этот цвет приходит данными.
   */
  accent?: string | undefined;
}

/** Больше двадцати частиц на 96 пикселях кружка сливаются в кашу. */
const COUNT = 14;

/**
 * Золотой угол. Раскладка по нему не оставляет ни просветов, ни сгущений —
 * тем же приёмом природа расставляет семена в подсолнухе.
 */
const GOLDEN_ANGLE = 137.508;

/** Разлёт в пикселях: от края кружка (48) до полутора его радиусов. */
const MIN_DISTANCE = 52;
const MAX_DISTANCE = 104;

/** Частицы падают: конечная точка ниже радиальной на эту величину. */
const GRAVITY = 22;

interface Particle {
  dx: number;
  dy: number;
  spin: number;
  size: number;
  /** Доля задержки внутри одного шага каскада. */
  offset: number;
}

function particles(): Particle[] {
  return Array.from({ length: COUNT }, (_, index) => {
    const angle = ((index * GOLDEN_ANGLE) % 360) * (Math.PI / 180);
    // Дистанция гуляет по индексу: ровное кольцо читалось бы как рябь,
    // а не как разлёт.
    const spread = (index % 5) / 4;
    const distance = MIN_DISTANCE + (MAX_DISTANCE - MIN_DISTANCE) * spread;

    return {
      dx: Math.cos(angle) * distance,
      dy: Math.sin(angle) * distance + GRAVITY,
      spin: index % 2 === 0 ? 180 : -140,
      size: index % 3 === 0 ? 8 : 5,
      offset: (index % 4) / 4,
    };
  });
}

export function Celebration({ accent }: CelebrationProps) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const root = ref.current;
    if (!root || !canAnimate()) return;

    const nodes = root.querySelectorAll<HTMLElement>("[data-particle]");
    const list = particles();

    const running = [...nodes].map((node, index) => {
      const particle = list[index];
      if (!particle) return undefined;

      return animate(
        node,
        [
          { transform: "translate3d(0, 0, 0) scale(0.4) rotate(0deg)", opacity: 0, offset: 0 },
          { transform: "translate3d(0, 0, 0) scale(1) rotate(0deg)", opacity: 1, offset: 0.12 },
          {
            transform: `translate3d(${String(particle.dx)}px, ${String(particle.dy)}px, 0) scale(0.3) rotate(${String(particle.spin)}deg)`,
            opacity: 0,
            offset: 1,
          },
        ],
        {
          duration: duration("page") + duration("slow"),
          easing: easing("out-quint"),
          // Каскад запуска: одновременный старт всех четырнадцати выглядит
          // взрывом хлопушки, разнесённый — салютом.
          delay: duration("fast") + particle.offset * duration("stagger"),
        },
      );
    });

    return () => {
      for (const animation of running) animation?.cancel();
    };
  }, []);

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
    >
      {particles().map((particle, index) => (
        <span
          key={index}
          data-particle
          className={[
            "absolute rounded-full",
            // Через одну — акцент интерфейса: одноцветный салют выглядит
            // осыпавшейся краской, двухцветный — светом.
            accent ? "" : index % 2 === 0 ? "bg-status-profit" : "bg-action-primary",
          ].join(" ")}
          style={{
            width: `${String(particle.size)}px`,
            height: `${String(particle.size)}px`,
            // Ноль по умолчанию: без Web Animations API и при уменьшенном
            // движении частицы просто не показываются, а не залипают точками
            // в центре кружка.
            opacity: 0,
            ...(accent
              ? { backgroundColor: index % 2 === 0 ? accent : "var(--color-action-primary)" }
              : {}),
          }}
        />
      ))}
    </span>
  );
}
